import './Navigation.scss';

import { Nav, NavDropdown, Navbar } from 'react-bootstrap';
import React, { MouseEventHandler, useContext } from 'react';
import { faCaretDown, faFlask, faUserCircle } from '@fortawesome/free-solid-svg-icons';

import { AppContext } from '~/AppContext';
import { BEM } from '@feedfm/feed-util';
import { CreateAppModal } from '~/components/CreateAppModal';
import { CreateUserModal } from '../UserPage/CreateUserModal/CreateUserModal';
import FeedfmLogo from '~/images/Logos_2025_feed.fm_logo_white.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { NavLink } from 'react-router-dom';
import { OmniSearch } from '~/components/OmniSearch/OmniSearch';
import { RestrictedAccess } from '~/components/RestrictedAccess/RestrictedAccess';
import { featureFlags } from '~/featureFlags';
import { logout } from '~/components/Bouncer/Bouncer';
import pkg from '../../../package.json';
import { useFeatureFlag } from '~/hooks/useFeatureFlag';
import { useModal } from '~/hooks/useModal';

export interface NavigationProps {
  /**
   * This for rendering errors that occur above <ApolloProvider>. safeMode omits rendering any
   * components that will fail without ApolloProvider.
   */
  safeMode?: boolean;
}

export function Navigation( { safeMode = false }: NavigationProps ) {
  const bem = new BEM( 'ops-navigation' );

  const showDeveloperTools = useFeatureFlag( featureFlags, 'developer-tools' );

  const showUserModal = useModal();
  const showAppModal = useModal();

  const handleLogout: MouseEventHandler = ( event ) => {
    event.preventDefault();

    logout( '/' );
  };

  const {
    me,
    backend: { featureBranch, isLocal, isProduction, version },
  } = useContext( AppContext );

  const isStage = !isLocal && !isProduction;
  const isLocallyHosted = window.location.hostname.match( /(localhost|local.feed.fm)/ );
  const versionCssMods = {
    'production-env': isProduction,
    'local-env': isLocal,
    'stage-env': isStage,
    'locally-hosted': isLocallyHosted,
  };

  const featureSlug = featureBranch ? <>, { featureBranch }</> : null;

  const clientVersion = pkg.version;

  return (
    <Navbar bg="primary" variant="dark" expand="lg" className={ BEM.$( bem ) }>
      <Navbar.Brand href="/" title="Home">
        <FeedfmLogo height="33" style={ { marginRight: '.75rem' } } />
      </Navbar.Brand>
      <Navbar.Toggle aria-controls="nav-collapsible" />
      <Navbar.Collapse id="nav-collapsible">
        <div className={ BEM.$( bem.el( 'version', versionCssMods ) ) }>
          <div className={ BEM.$( bem.el( 'frontend-version' ) ) }>Client: { clientVersion }</div>
          <div className={ BEM.$( bem.el( 'backend-version' ) ) }>
            Server: { version }
            { isLocallyHosted && (
              <span className={ BEM.$( bem.el( 'environment' ) ) }>
                { isProduction && <span>⚠️ production</span> }
                { isStage && <span>(stage{ featureSlug })</span> }
                { isLocal && <span>(local{ featureSlug })</span> }
              </span>
            ) }
          </div>
        </div>
        <Nav className="mr-auto">
          <NavDropdown title="Catalog" id="catalog">
            <NavDropdown.Item as={ NavLink } to="/catalog/search">
              Music Catalog
            </NavDropdown.Item>
            <NavDropdown.Item as={ NavLink } to="/availability-review/by-audio-file">
              Tracks Losing Rights
            </NavDropdown.Item>
            <NavDropdown.Item as={ NavLink } to="/bulk-upload">
              Bulk Upload
            </NavDropdown.Item>
          </NavDropdown>
          <NavDropdown title="Curation" id="curation">
            <NavDropdown.Item as={ NavLink } to="/curated-stations">
              Curated Stations
            </NavDropdown.Item>
            <NavDropdown.Item
              href="https://app.asana.com/1/53286908647491/project/1204505661996303/calendar/1205117269020243"
              target="_blank"
              rel="noopener noreferrer"
            >
              Optimization Calendar
            </NavDropdown.Item>
            <NavDropdown.Item as={ NavLink } to="/dashboard">
              Station Requests
            </NavDropdown.Item>
            <NavDropdown.Item as={ NavLink } to="/publishing">
              Publishing Status
            </NavDropdown.Item>
            <NavDropdown.Item as={ NavLink } to="/clips/collections/sort">
              Clips Collection Sorting
            </NavDropdown.Item>
          </NavDropdown>
          <Nav.Link as={ NavLink } to="/analytics">
            Analytics
          </Nav.Link>
          <NavDropdown title="Rights" id="rights">
            <NavDropdown.Item as={ NavLink } to="/content/streaming-rights">
              Streaming Rights
            </NavDropdown.Item>
            <NavDropdown.Item as={ NavLink } to="/content/sources">
              Content Sources
            </NavDropdown.Item>
            <NavDropdown.Item as={ NavLink } to="/content/tracks">
              Track details
            </NavDropdown.Item>
            <NavDropdown.Item as={ NavLink } to="/filters">
              Filters
            </NavDropdown.Item>
            <RestrictedAccess permissions={ ['report:read'] }>
              <NavDropdown.Item as={ NavLink } to="/report">
                Reporting
              </NavDropdown.Item>
            </RestrictedAccess>
          </NavDropdown>
          <RestrictedAccess permissions={ ['user:create'] }>
            <NavDropdown title="Customers" id="customers">
              <NavDropdown.Item
                onClick={ () => showUserModal( ( onHide ) => <CreateUserModal show={ true } onHide={ onHide } /> ) }
              >
                Create User
              </NavDropdown.Item>
              <NavDropdown.Item
                onClick={ () => showAppModal( ( onHide ) => <CreateAppModal show={ true } onHide={ onHide } /> ) }
              >
                Create App
              </NavDropdown.Item>
              <NavDropdown.Item as={ NavLink } to="/clips/customers">
                Clips Customers
              </NavDropdown.Item>
              <NavDropdown.Divider />
              <NavDropdown.Item as={ NavLink } to="/roles">
                Roles &amp; Permissions
              </NavDropdown.Item>
            </NavDropdown>
          </RestrictedAccess>
          { /* Full-navigation anchor (not a react-router NavLink): hands off to the
               separate Content Ops app, served from its own S3 bucket via the
               `/content-ops/*` CloudFront behavior. The trailing slash matters: a
               bare "/content-ops" doesn't match that behavior, so it would 404 into
               the host SPA fallback and bounce to /home. "/content-ops/" matches and
               is rewritten to index.html by the subdir-index viewer-request function
               on that behavior (present on prod, stage, and PR-preview distributions).
               content-ops uses HashRouter, so deep links live in the # fragment.
               Visible to any authenticated ops user, like the Catalog pages. */ }
          <Nav.Link href="/content-ops/">Content Ops</Nav.Link>
        </Nav>
        { safeMode === false && <OmniSearch className="omnisearch col-4 pr-3" /> }

        { showDeveloperTools && (
          <Nav.Link href="/developer-tools" className={ BEM.$( 'developer-tools-link' ) }>
            <FontAwesomeIcon icon={ faFlask } />
          </Nav.Link>
        ) }

        <NavDropdown
          alignRight
          title={
            <span className={ BEM.$( bem.el( 'user-dropdown-title' ) ) }>
              <FontAwesomeIcon icon={ faUserCircle } />
              <span className="ml-2">{ me?.emailAddress }</span>
              <FontAwesomeIcon icon={ faCaretDown } className="ml-2" />
            </span>
          }
          id="user-dropdown"
          className={ BEM.$( bem.el( 'user-dropdown' ) ) }
        >
          <NavDropdown.Item as={ NavLink } to={ `/user/${me?.id}` }>
            User
          </NavDropdown.Item>
          <NavDropdown.Item onClick={ handleLogout }>Logout</NavDropdown.Item>
        </NavDropdown>
      </Navbar.Collapse>
    </Navbar>
  );
}
